import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faCheck,
  faDownload,
  faEye,
  faEyeSlash,
  faFileArrowUp,
  faLock,
  faPaperclip,
  faSpinnerThird,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';

import { ContextMenu } from '../../../shared/components/ui/context-menu/context-menu';
import { ContextMenuTriggerDirective } from '../../../shared/components/ui/context-menu/context-menu-trigger.directive';
import { SquareIconBtn } from '../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';
import { Button } from '../../../shared/components/ui/buttons/gradient-btn/button';
import { TooltipPosition } from '../../../shared/components/ui/tooltip/tooltip.directive';
import { FileDropZone } from '../../../shared/components/file-upload/file-drop-zone/file-drop-zone';
import { fileTypeIcon } from '../../../shared/helpers/file-type-icon';
import { formatBytes } from '../../../shared/helpers/upload-image';
import { RoomsStore } from '../../../state/rooms.repository';
import { RoomFile, RoomFileProtection, RoomFileRetention } from '../room-file';
import { RoomFilesService } from '../room-files.service';
import { RoomFileDownloadController } from '../room-file-download';
import { LanguageService } from '../../../core/services/language.service';
import { LocalizedDatePipe } from '../../../shared/pipes/localized-date.pipe';

const RETENTION_OPTIONS: { value: RoomFileRetention; label: string }[] = [
  { value: '1h', label: _('1 hour') },
  { value: '1d', label: _('1 day') },
  { value: '1w', label: _('1 week') },
  { value: 'room', label: _('Until the room is deleted') },
];

/**
 * Paperclip button + menu for the files shared in the room: everyone lists
 * and downloads them; users with `shareFiles` upload (optionally encrypted,
 * with a retention period) and delete them. State lives in
 * `RoomFilesService`, so uploads continue while the menu is closed.
 */
@Component({
  selector: 'andor-room-files',
  standalone: true,
  imports: [
    LocalizedDatePipe,
    FaDuotoneIconComponent,
    TranslatePipe,
    ContextMenu,
    ContextMenuTriggerDirective,
    SquareIconBtn,
    Button,
    FileDropZone,
  ],
  templateUrl: './room-files.html',
  styleUrl: './room-files.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RoomFileDownloadController],
})
export class RoomFiles {
  readonly tooltipPosition = input<TooltipPosition>('below');
  readonly menuXPosition = input<'before' | 'after'>('before');
  readonly menuYPosition = input<'above' | 'below'>('below');

  protected readonly filesService = inject(RoomFilesService);
  protected readonly downloads = inject(RoomFileDownloadController);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _translate = inject(TranslateService);
  // Read before `instant()` so labels re-translate when the language changes.
  private readonly _lang = inject(LanguageService).active;
  private readonly _rooms = toSignal(inject(RoomsStore).rooms$, { initialValue: [] });

  protected readonly faPaperclip = faPaperclip;
  protected readonly faFileArrowUp = faFileArrowUp;
  protected readonly faDownload = faDownload;
  protected readonly faTrashCan = faTrashCan;
  protected readonly faCheck = faCheck;
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faXmark = faXmark;
  protected readonly faLock = faLock;
  protected readonly faEye = faEye;
  protected readonly faEyeSlash = faEyeSlash;
  protected readonly formatBytes = formatBytes;
  protected readonly fileTypeIcon = fileTypeIcon;
  protected readonly retentionOptions = RETENTION_OPTIONS;

  protected readonly showPassword = signal(false);
  /** Deleting affects everyone in the room: the first click only arms it. */
  protected readonly confirmingDelete = signal<string | null>(null);
  private _confirmTimer: ReturnType<typeof setTimeout> | undefined;

  protected readonly dropHint = computed(() => {
    this._lang();
    return this._translate.instant('Drop files here or click to choose (max. [max] each)', {
      max: formatBytes(this.filesService.limits().maxFileBytes, 0),
    });
  });

  /** What "until the room is deleted" means for the joined room. */
  protected readonly retentionHint = computed(() => {
    const roomId = this.filesService.roomId();
    const room = this._rooms().find((r) => r.roomId === roomId);
    // Hidden (instant) rooms are not in the room list.
    if (!room || room.isHidden) {
      return _('Files go with the room a few minutes after everyone has left.');
    }
    if (room.schedule && !room.schedule.recurrence) {
      return _('Files go with the room once the scheduled meeting is over.');
    }
    return _('Files are kept until someone closes the room.');
  });

  protected readonly protectionOptions = computed(() => {
    const options: { value: RoomFileProtection; label: string }[] = [
      { value: 'none', label: _('None') },
    ];
    if (this.filesService.roomHasPassword()) {
      options.push({ value: 'room', label: _('Room password') });
    }
    options.push({ value: 'custom', label: _('Own password') });
    return options;
  });

  protected readonly protectionHint = computed(() => {
    switch (this.filesService.draft()?.protection) {
      case 'room':
        return _('Encrypted in your browser. Everyone with the room password can open it.');
      case 'custom':
        return _(
          'Encrypted in your browser. Share the password with whoever should open the file — it cannot be recovered.'
        );
      default:
        return _('Stored unencrypted. Everyone in the room can open it.');
    }
  });

  protected readonly canUpload = computed(() => {
    const draft = this.filesService.draft();
    if (!draft) return false;
    if (draft.protection === 'custom' && draft.password.length === 0) return false;
    return draft.files.some((file) => !this.filesService.draftProblem(file));
  });

  protected onMenuOpened() {
    void this.filesService.refresh();
  }

  protected setPassword(event: Event) {
    this.filesService.updateDraft({ password: (event.target as HTMLInputElement).value });
  }

  protected upload() {
    if (!this.canUpload()) return;
    this.showPassword.set(false);
    this.filesService.uploadDraft();
  }

  protected uploaderLabel(file: RoomFile): string {
    if (this.filesService.isOwn(file)) return this._translate.instant('You');
    return file.uploaderName || this._translate.instant('Someone');
  }

  protected expiryLabel(file: RoomFile): string {
    if (file.expiresAt === null) return '';
    const minutes = Math.max(1, Math.round((file.expiresAt - Date.now()) / 60_000));
    if (minutes < 60) return this._translate.instant('[count] min left', { count: minutes });
    const hours = Math.round(minutes / 60);
    if (hours < 48) return this._translate.instant('[count] h left', { count: hours });
    return this._translate.instant('[count] days left', { count: Math.round(hours / 24) });
  }

  protected async onDelete(file: RoomFile) {
    clearTimeout(this._confirmTimer);
    if (this.confirmingDelete() !== file.id) {
      this.confirmingDelete.set(file.id);
      this._confirmTimer = setTimeout(() => this.confirmingDelete.set(null), 4000);
      return;
    }
    this.confirmingDelete.set(null);
    if (!(await this.filesService.remove(file))) {
      this._snackBar.open(
        this._translate.instant('"[name]" could not be deleted.', { name: file.name }),
        undefined,
        { duration: 3000, horizontalPosition: 'center', verticalPosition: 'top' }
      );
    }
  }
}
